import EditAutomaticContainersModalComponent from '@/features/pos/components/Cart/parts/EditAutomaticContainersModal/Component'
import { CartProductItem } from '@/features/pos/components/Cart/parts/Overview/parts/CartItem/types'
import { AutomaticContainerRemovals } from '@/features/pos/components/Cart/utils/automatic-containers'
import { AutomaticContainerRemovalsInput } from '@/features/pos/components/OrderFinalization/form/types'
import { useCloseOnPosModeChange } from '@/features/pos/hooks/useCloseOnPosModeChange'
import { useScreenLayout } from '@/hooks/screen'
import { FC, useCallback, useMemo, useState } from 'react'

interface EditAutomaticContainersModalProps {
    item: CartProductItem
    automaticContainerRemovals: AutomaticContainerRemovalsInput
    onAutomaticContainerRemovalsChange: (removals: AutomaticContainerRemovalsInput) => void
    onClose: () => void
}

const emptyContainers: NonNullable<CartProductItem['containers']> = []

const EditAutomaticContainersModal: FC<EditAutomaticContainersModalProps> = ({
    item,
    automaticContainerRemovals,
    onAutomaticContainerRemovalsChange,
    onClose,
}) => {
    const layout = useScreenLayout()
    const containers = item.containers ?? emptyContainers
    const [stagedContainerIds, setStagedContainerIds] = useState<string[]>(() => {
        const removals = AutomaticContainerRemovals.from(automaticContainerRemovals)

        return containers.filter(({ source }) => removals.has(source)).map(({ id }) => id)
    })

    const [activeContainers, containersToRemove] = useMemo(
        () => [
            containers.filter(({ id }) => !stagedContainerIds.includes(id)),
            containers.filter(({ id }) => stagedContainerIds.includes(id)),
        ],
        [containers, stagedContainerIds]
    )

    const handleClose = useCallback(onClose, [onClose])

    const handleStageRemoval = useCallback((containerId: string) => {
        setStagedContainerIds((containerIds) =>
            containerIds.includes(containerId) ? containerIds : [...containerIds, containerId]
        )
    }, [])

    const handleRestore = useCallback((containerId: string) => {
        setStagedContainerIds((containerIds) => containerIds.filter((id) => id !== containerId))
    }, [])

    const handleConfirm = useCallback(() => {
        onAutomaticContainerRemovalsChange(
            AutomaticContainerRemovals.from(automaticContainerRemovals)
                .replaceFor(containers, containersToRemove)
                .toArray()
        )
        handleClose()
    }, [
        automaticContainerRemovals,
        containers,
        containersToRemove,
        handleClose,
        onAutomaticContainerRemovalsChange,
    ])

    useCloseOnPosModeChange(handleClose)

    return (
        <EditAutomaticContainersModalComponent
            activeContainers={activeContainers}
            containersToRemove={containersToRemove}
            layout={layout}
            onStageRemoval={handleStageRemoval}
            onRestore={handleRestore}
            onCancel={handleClose}
            onConfirm={handleConfirm}
        />
    )
}

export default EditAutomaticContainersModal
